/** Volto Project Configuration
 * The recommended way for configuring a Volto project is using an add-on.
 * The project should only be considered as boilerplate, and all the code and
 * configuration should happen and be placed in add-ons.
 *
 * Both configuring directly and placing code in a project is discouraged, and might be
 * removed at some point from Volto.
 *
 * The local project is left for backwards compatibility for existing projects.
 */

import { defineMessages } from 'react-intl';
import BackToTop from './components/BackToTop';

import applyConfigBlockVariations from './components/BlockTemplates';

import { registerBlocks } from './components/Blocks/registerBlocks';
import { registerWidgets } from './components/Widgets/registerWidgets';

// [Internal] All the imports of modules required for the configuration *must* happen
// here BEFORE the following line
import '@plone/volto/config';
import { GA } from '@serpro/google-analytics/components';
import { withLgpdCookie } from 'lgpd.plugin/components';
import registerSlotComponents from './slotComponents';

defineMessages({
  Advanced: {
    id: 'advanced',
    defaultMessage: 'Advanced',
  },
  contentProperties: {
    id: 'contentProperties',
    defaultMessage: 'Content properties',
  },
  contentTitle: {
    id: 'Content title',
    defaultMessage: 'Content title',
  },
  contentDescription: {
    id: 'Content description',
    defaultMessage: 'Content description',
  },
  leadimage: {
    id: 'Lead image field',
    defaultMessage: 'Lead image field',
  },
  searchBlock: {
    id: 'Search block title',
    defaultMessage: 'Search',
  },
  carousel: {
    id: 'Carousel',
    defaultMessage: 'Carousel',
  },
});

const hiddenDefaultBlocks = ['maps', 'gridBlock', 'slateTable', 'hero', 'toc'];

export default function applyConfig(config) {
  registerWidgets(config);
  registerBlocks(config);

  // Remove blocos padrão do Volto que não serão usados pelo portal
  hiddenDefaultBlocks.forEach(
    (block) => delete config.blocks.blocksConfig[block],
  );

  config.settings = {
    ...config.settings,

    // Define o idioma do site
    supportedLanguages: ['pt-br', 'en', 'es'],
    defaultLanguage: 'pt-br',
    isMultilingual: true,

    // Altera a profundidade do menu
    navDepth: 3,

    showRelatedItems: true,
    defaultPageSize: 6,
    searchPageSize: 25,

    appExtras: [
      ...config.settings.appExtras,
      {
        match: '',
        component: withLgpdCookie(GA),
      },
      //   // {
      //   //   match: '',
      //   //   component: GTMVolto,
      //   // },
      {
        match: '',
        component: BackToTop,
      },
    ],

    htmlScriptAllowedDomains: [
      'twitter.com/',
      'instagram.com/',
      'youtube.com/',
    ],
  };

  config.blocks = {
    ...config.blocks,

    // Remove obrigatoriedade do bloco de título
    requiredBlocks: [],

    // Adiciona grupos Metadados e Portal G20
    // Altera ordem dos grupos
    // Para traduzir, o id do bloco deve corresponder ao id da mensagem
    groupBlocksOrder: [
      { id: 'mostUsed', title: 'Most used' },
      { id: 'contentProperties', title: 'Content properties' },
      { id: 'text', title: 'Text' },
      { id: 'layout', title: 'Layout' },
      { id: 'listing', title: 'Listing' },
      { id: 'media', title: 'Media' },
      { id: 'portalcop30', title: 'Portal COP30' },
      { id: 'advanced', title: 'Advanced' },
      ...config.blocks.groupBlocksOrder,
    ].filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id),
    ),
  };

  // Adiciona o bloco de colunas ao grupo Mais Usados
  config.blocks.blocksConfig.columnsBlock.mostUsed = true;

  // Remove o bloco de listagem do grupo Mais Usados
  config.blocks.blocksConfig.listing.mostUsed = false;

  // Move blocos padrão do Volto para os grupos definidos para o projeto
  config.blocks.blocksConfig.slate.group = 'text';
  config.blocks.blocksConfig.title.group = 'contentProperties';
  config.blocks.blocksConfig.description.group = 'contentProperties';
  config.blocks.blocksConfig.leadimage.group = 'contentProperties';
  config.blocks.blocksConfig.listing.group = 'listing';
  config.blocks.blocksConfig.search.group = 'listing';
  config.blocks.blocksConfig.teaser.group = 'layout';
  // config.blocks.blocksConfig.slateTable.group = 'layout';
  config.blocks.blocksConfig.html.group = 'advanced';
  config.blocks.blocksConfig.columnsBlock.group = 'layout';

  // Remove links dos blocos de título
  config.settings.slate.useLinkedHeadings = false;

  // Seta valor default para cabeçalho
  // config.blocks.blocksConfig.heading.default_tag = 'h2';

  // Altera o grupo do bloco Carousel
  config.blocks.blocksConfig.carousel.group = 'layout';

  // Altera o grupo do bloco Accordion
  config.blocks.blocksConfig.accordion.group = 'layout';

  config.blocks.blocksConfig.accordion.schemaEnhancer = null;

  // Altera nomes dos blocos do conteúdo
  config.blocks.blocksConfig.title.title = 'Content title';
  config.blocks.blocksConfig.description.title = 'Content description';
  config.blocks.blocksConfig.leadimage.title = 'Lead image field';
  config.blocks.blocksConfig.carousel.title = 'Carousel';

  // Altera nome do bloco de Busca
  config.blocks.blocksConfig.search.title = 'Search block title';

  // Adiciona variações customizadas de blocos
  applyConfigBlockVariations(config);

  // Desabilita alinhamento dos blocos de cabeçalho
  // config.blocks.blocksConfig.heading.hide_alignment = true;
  // config.blocks.blocksConfig.subject.hide_alignment = true;

  // Registra slots customizados
  registerSlotComponents(config);

  // Adiciona middleware da api cop30
  // cop30ApiMiddleware(config);

  return config;
}
